import { useCallback, useEffect, useRef, useState } from 'react';

import { useDictationContext } from './DictationProvider';

export type DictationState = 'idle' | 'recording' | 'transcribing';

/** A clip stops itself here: about 1.2 MB of opus at 32 kbps. */
export const MAX_CLIP_MS = 5 * 60 * 1000;
/** A clip shorter than this is a mis-press: nothing is sent. */
export const MIN_CLIP_MS = 500;

const PREFERRED_MIME = 'audio/webm;codecs=opus';
const FALLBACK_MIME = 'audio/mp4';
const BITS_PER_SECOND = 32_000;

export interface UseDictationOptions {
  /** Stable code of the field (`chat.composer`), sent with the clip. */
  field: string;
  hint?: string;
  /** The clip's words, trimmed and non-empty. */
  onTranscript(text: string): void;
}

export interface UseDictation {
  state: DictationState;
  /** Why the last attempt failed, for the mic's title. */
  error?: string;
  start(): Promise<void>;
  stop(): void;
}

/**
 * Record from the microphone until `stop` (or five minutes), then hand the
 * clip to the provider's `transcribe`. idle → recording → transcribing → idle.
 * Under no provider, `start` does nothing.
 */
export function useDictation({ field, hint, onTranscript }: UseDictationOptions): UseDictation {
  const dictation = useDictationContext();
  const [state, setState] = useState<DictationState>('idle');
  const [error, setError] = useState<string | undefined>(undefined);

  // The latest props, read when the clip ends rather than when it started.
  const latest = useRef({ dictation, field, hint, onTranscript });
  latest.current = { dictation, field, hint, onTranscript };

  const busy = useRef(false);
  const mounted = useRef(true);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAt = useRef(0);
  const stoppedAt = useRef(0);

  const release = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  const settle = useCallback(() => {
    busy.current = false;
    recorder.current = null;
    if (mounted.current) setState('idle');
  }, []);

  const stop = useCallback(() => {
    const active = recorder.current;
    if (!active || active.state === 'inactive') return;
    stoppedAt.current = Date.now();
    active.stop();
  }, []);

  const start = useCallback(async () => {
    const provider = latest.current.dictation;
    if (!provider || busy.current) return;
    setError(undefined);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Recording not supported in this browser');
      return;
    }
    busy.current = true;

    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
    } catch (reason) {
      const name = (reason as { name?: string } | null)?.name ?? '';
      if (name === 'NotAllowedError') {
        provider.onPermissionDenied?.();
        if (mounted.current) setError('Microphone not allowed');
      } else if (name === 'NotFoundError') {
        if (mounted.current) setError('No microphone found');
      } else if (mounted.current) {
        setError('Could not start the microphone');
      }
      settle();
      return;
    }
    if (!mounted.current) {
      media.getTracks().forEach((track) => track.stop());
      busy.current = false;
      return;
    }
    stream.current = media;

    const chunks: Blob[] = [];
    let mimeType = FALLBACK_MIME;
    let rec: MediaRecorder;
    try {
      mimeType = MediaRecorder.isTypeSupported(PREFERRED_MIME) ? PREFERRED_MIME : FALLBACK_MIME;
      rec = new MediaRecorder(media, { mimeType, audioBitsPerSecond: BITS_PER_SECOND });
      rec.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      rec.onstop = () => {
        release();
        const length = (stoppedAt.current || Date.now()) - startedAt.current;
        if (!mounted.current || length < MIN_CLIP_MS || chunks.length === 0) {
          settle();
          return;
        }
        const blob = new Blob(chunks, { type: rec.mimeType || mimeType });
        const { dictation: now, field: f, hint: h } = latest.current;
        if (!now) {
          settle();
          return;
        }
        const failed = () => {
          if (mounted.current) setError('Could not transcribe that. Try again.');
        };
        setState('transcribing');
        let pending: Promise<string>;
        try {
          pending = now.transcribe(blob, f, h);
        } catch {
          failed();
          settle();
          return;
        }
        Promise.resolve(pending)
          .then((text) => {
            const words = (text ?? '').trim();
            if (words && mounted.current) latest.current.onTranscript(words);
          })
          .catch(failed)
          .finally(settle);
      };
      recorder.current = rec;
      startedAt.current = Date.now();
      stoppedAt.current = 0;
      rec.start();
    } catch {
      recorder.current = null;
      release();
      if (mounted.current) setError('Recording not supported in this browser');
      settle();
      return;
    }
    timer.current = setTimeout(stop, MAX_CLIP_MS);
    setState('recording');
  }, [release, settle, stop]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const active = recorder.current;
      if (active && active.state !== 'inactive') active.stop();
      release();
    };
  }, [release]);

  return { state, error, start, stop };
}
